import { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Camera, Upload, X, RotateCcw, Check } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ProfilePhotoUpload({ 
  currentImage, 
  userName, 
  onImageUpdate,
  size = 'lg' // 'sm', 'md', 'lg'
}) {
  const [showDialog, setShowDialog] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [stream, setStream] = useState(null);
  
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-20 h-20',
    lg: 'w-24 h-24'
  };

  const iconSizes = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12'
  };

  const textSizes = {
    sm: 'text-xl',
    md: 'text-2xl',
    lg: 'text-3xl'
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      setStream(mediaStream);
      setShowCamera(true);
      
      // Wait for video element to be available
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (error) {
      console.error('Camera error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Camera access denied. Please allow camera access in your browser settings.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No camera found on this device.');
      } else {
        toast.error('Failed to access camera. Please try uploading a file instead.');
      }
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw video frame to canvas (mirror for selfie)
    context.translate(canvas.width, 0);
    context.scale(-1, 1);
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Get image data
    const imageData = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedImage(imageData);
    stopCamera();
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    startCamera();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setCapturedImage(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadImage = async () => {
    if (!capturedImage) return;
    
    setUploading(true);
    try {
      // Convert base64 to blob
      const response = await fetch(capturedImage);
      const blob = await response.blob();
      
      const formData = new FormData();
      formData.append('file', blob, 'profile-photo.jpg');
      
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/auth/profile/image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      onImageUpdate?.(res.data.url);
      toast.success('Profile photo updated!');
      handleClose();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    stopCamera();
    setCapturedImage(null);
    setShowDialog(false);
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http') || image.startsWith('data:')) return image;
    if (image.startsWith('/')) return `${BACKEND_URL}${image}`;
    // For Emergent storage paths (e.g., handybayani/profiles/xxx.png)
    const token = localStorage.getItem('token');
    return `${API}/files/${image}?auth=${token}`;
  };

  return (
    <>
      {/* Profile Photo Display */}
      <div 
        className="relative cursor-pointer group"
        onClick={() => setShowDialog(true)}
        data-testid="profile-photo-trigger"
      >
        <div className={`${sizeClasses[size]} bg-amber-500 rounded-full flex items-center justify-center overflow-hidden`}>
          {currentImage ? (
            <img 
              src={getImageUrl(currentImage)} 
              alt="Profile" 
              className="w-full h-full object-cover"
            />
          ) : (
            <span className={`${textSizes[size]} font-bold text-slate-900`}>
              {userName?.charAt(0) || 'U'}
            </span>
          )}
        </div>
        <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <Camera className="w-6 h-6 text-white" />
        </div>
      </div>

      {/* Photo Capture Dialog */}
      <Dialog open={showDialog} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Update Profile Photo</DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            {/* Camera View */}
            {showCamera && !capturedImage && (
              <div className="relative">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full rounded-xl bg-black"
                  style={{ transform: 'scaleX(-1)' }}
                />
                <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-12 h-12 rounded-full bg-white/90 hover:bg-white"
                    onClick={stopCamera}
                  >
                    <X className="w-5 h-5" />
                  </Button>
                  <Button
                    size="icon"
                    className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600"
                    onClick={capturePhoto}
                    data-testid="capture-photo-btn"
                  >
                    <Camera className="w-8 h-8 text-white" />
                  </Button>
                </div>
              </div>
            )}

            {/* Captured/Selected Image Preview */}
            {capturedImage && (
              <div className="space-y-4">
                <div className="relative">
                  <img 
                    src={capturedImage} 
                    alt="Preview" 
                    className="w-full rounded-xl"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={retakePhoto}
                    disabled={uploading}
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Retake
                  </Button>
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={uploadImage}
                    disabled={uploading}
                    data-testid="confirm-photo-btn"
                  >
                    {uploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Use Photo
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Options (when not in camera mode and no captured image) */}
            {!showCamera && !capturedImage && (
              <div className="space-y-4">
                <div className="flex flex-col gap-3">
                  <Button
                    className="w-full h-14 bg-slate-900 hover:bg-slate-800"
                    onClick={startCamera}
                    data-testid="open-camera-btn"
                  >
                    <Camera className="w-5 h-5 mr-3" />
                    Take a Selfie
                  </Button>
                  
                  <div className="relative">
                    <Button
                      variant="outline"
                      className="w-full h-14"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="upload-file-btn"
                    >
                      <Upload className="w-5 h-5 mr-3" />
                      Upload from Gallery
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                </div>

                <p className="text-xs text-center text-slate-500">
                  Supported formats: JPG, PNG, GIF. Max 5MB.
                </p>
              </div>
            )}

            {/* Hidden canvas for capturing */}
            <canvas ref={canvasRef} className="hidden" />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
